/**
 * SecureEmbed User Profile Page
 *
 * Features:
 * - View and edit profile information
 * - Change password via Cognito
 * - Role display (Administrator for admin users)
 * - Data source permissions management
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Mail, Building, Shield, Calendar, Edit2, Save, X, Key, CheckCircle, AlertTriangle, Loader2, Database, Lock, Unlock, Zap, TrendingUp, Activity, RefreshCw } from 'lucide-react';
import { useAuth } from '../App';
import { STORAGE_KEYS } from '../constants/config';
import { datastoreApi } from '../services/api';

// Available data sources for permissions
const AVAILABLE_DATA_SOURCES = [
  { id: 'aemo', name: 'AEMO Energy Market', collection: 'Electricity Data', icon: Zap },
  { id: 'aer', name: 'AER Compliance', collection: 'Electricity Data', icon: Shield },
  { id: 'bom-weather', name: 'BOM Weather', collection: 'Environmental Data', icon: Activity },
  { id: 'asx-energy', name: 'ASX Energy Futures', collection: 'Financial Data', icon: TrendingUp },
];

// Maps each profile data-source to its backend datastore key so availability
// ("Active" vs "Coming Soon") is derived from live record counts, not hardcoded.
const SOURCE_DATASTORE_KEYS: Record<string, string> = {
  'aemo': 'aemo',
  'aer': 'aer',
  'bom-weather': 'bom',
  'asx-energy': 'asx-energy',
};

// Get Cognito config from window
const getConfig = (): { userPoolId?: string; clientId?: string; region?: string } => {
  const config = (window as any).SECUREEMBED_CONFIG || {};
  return {
    userPoolId: config.COGNITO_USER_POOL_ID || config.USER_POOL_ID,
    clientId: config.COGNITO_CLIENT_ID || config.USER_POOL_CLIENT_ID,
    region: config.REGION || 'ap-southeast-2'
  };
};

const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  // H9: AbortController for fetch cleanup
  const abortControllerRef = useRef<AbortController | null>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [editedEmail, setEditedEmail] = useState(user?.email || '');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveStatus, setSaveStatus] = useState<'success' | 'error' | ''>('');

  // Data source permissions
  const [dataSourcePermissions, setDataSourcePermissions] = useState<string[]>([]);

  // User role from settings API
  const [userRole, setUserRole] = useState<string | null>(null);
  const [, setUserEmail] = useState<string | null>(null);
  const [, setUserGroups] = useState<string[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  // Real account-creation date (from Cognito UserCreateDate via /settings) and
  // live per-datastore record counts used to derive data-source availability.
  const [memberSince, setMemberSince] = useState<string | null>(null);
  const [sourceCounts, setSourceCounts] = useState<Record<string, number> | null>(null);

  // Fetch user settings from API to get role
  const fetchUserSettings = useCallback(async () => {
    try {
      setRefreshing(true);
      // ID token first — the API Gateway Cognito authorizer validates ID
      // tokens; sending the access token (AUTH_TOKEN) 401s. Same preference
      // apiClient uses, which is why every other page worked (R7, #4538).
      const token =
        localStorage.getItem(STORAGE_KEYS.ID_TOKEN) ||
        localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      if (!token) return;

      const config = (window as any).SECUREEMBED_CONFIG || {};
      const apiUrl = config.API_URL || config.API_ENDPOINT || '';

      // H9: Cancel any in-flight request before starting new one
      if (abortControllerRef.current) abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();

      const response = await fetch(`${apiUrl}/api/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: abortControllerRef.current.signal,
      });

      if (response.ok) {
        const data = await response.json();
        if (data.role) {
          setUserRole(data.role);
        }
        if (data.email) {
          setUserEmail(data.email);
          setEditedEmail(data.email);
        }
        if (data.groups) {
          setUserGroups(data.groups);
        }
        if (data.created) {
          const created = new Date(data.created);
          if (!isNaN(created.getTime())) {
            setMemberSince(created.toLocaleDateString('en-AU', { year: 'numeric', month: 'long' }));
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch user settings:', error);
    } finally {
      setRefreshing(false);
    }
  }, []);

  // Load permissions and fetch user settings on mount
  useEffect(() => {
    try {
      const storedUser = JSON.parse(localStorage.getItem(STORAGE_KEYS.USER) || '{}');
      // Default to ALL data sources (admins/admin-portal users have full visibility).
      setDataSourcePermissions(storedUser.dataSourcePermissions || AVAILABLE_DATA_SOURCES.map((s) => s.id));
    } catch {
      setDataSourcePermissions(['aemo']);
    }

    fetchUserSettings();

    // Live data-source availability from real datastore record counts.
    datastoreApi.getStats()
      .then((resp) => setSourceCounts(resp?.data?.record_counts || {}))
      .catch(() => setSourceCounts({}));

    // H9: Cleanup AbortController on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchUserSettings]);

  // Toggle data source permission
  const toggleDataSourcePermission = (sourceId: string) => {
    setDataSourcePermissions(prev => {
      const newPerms = prev.includes(sourceId)
        ? prev.filter(id => id !== sourceId)
        : [...prev, sourceId];

      // Save to localStorage
      const storedUser = JSON.parse(localStorage.getItem(STORAGE_KEYS.USER) || '{}');
      storedUser.dataSourcePermissions = newPerms;
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(storedUser));

      return newPerms;
    });
  };

  // Determine display role - admin users should show "Administrator"
  const getDisplayRole = () => {
    // First check role from settings API
    if (userRole) {
      const role = userRole.toLowerCase();
      if (role === 'admin' || role === 'administrator') {
        return 'Administrator';
      }
      return userRole.charAt(0).toUpperCase() + userRole.slice(1);
    }
    // Fallback to user context
    const role = user?.role?.toLowerCase();
    if (role === 'admin' || role === 'administrator' || user?.email?.includes('admin')) {
      return 'Administrator';
    }
    return user?.role || 'User';
  };

  const showSaveMessage = (message: string, status: 'success' | 'error') => {
    setSaveMessage(message);
    setSaveStatus(status);
    setTimeout(() => {
      setSaveMessage('');
      setSaveStatus('');
    }, 3000);
  };

  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const config = (window as any).SECUREEMBED_CONFIG || {};
      const apiUrl = config.API_URL || config.API_ENDPOINT || '';

      if (!token || !apiUrl) {
        showSaveMessage('Unable to update profile. Please log in again.', 'error');
        return;
      }

      const response = await fetch(`${apiUrl}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: editedEmail })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to update profile');
      }

      // Update user in localStorage
      const currentUser = JSON.parse(localStorage.getItem(STORAGE_KEYS.USER) || '{}');
      currentUser.email = editedEmail;
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(currentUser));

      showSaveMessage('Profile updated. Check your inbox to verify your email.', 'success');
      setIsEditing(false);
    } catch (error) {
      showSaveMessage(error instanceof Error ? error.message : 'Failed to update profile', 'error');
    }
  };

  const handleCancelEdit = () => {
    setEditedEmail(user?.email || '');
    setIsEditing(false);
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (!oldPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    setIsChangingPassword(true);

    try {
      const config = getConfig();
      const accessToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);

      if (!accessToken) {
        setPasswordError('Not authenticated. Please log in again.');
        return;
      }

      // Call Cognito ChangePassword API
      const response = await fetch(`https://cognito-idp.${config.region}.amazonaws.com/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.ChangePassword'
        },
        body: JSON.stringify({
          AccessToken: accessToken,
          PreviousPassword: oldPassword,
          ProposedPassword: newPassword
        })
      });

      if (response.ok) {
        setPasswordSuccess('Password changed successfully!');
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => {
          setShowPasswordModal(false);
          setPasswordSuccess('');
        }, 2000);
      } else {
        const errorData = await response.json();
        // M13: Don't expose Cognito exception types to user
        const errorType = errorData.__type?.split('#')[1] || errorData.message || '';
        if (errorType.includes('NotAuthorized')) {
          setPasswordError('Authentication failed. Please check your credentials.');
        } else if (errorType.includes('InvalidPassword')) {
          setPasswordError('New password does not meet requirements (min 8 chars, uppercase, lowercase, number)');
        } else {
          setPasswordError('An error occurred. Please try again.');
        }
        console.error('Password change failed', errorData);
      }
    } catch (err: any) {
      // M13: Generic error message for user
      setPasswordError('An error occurred. Please try again.');
      console.error('Password change failed', err);
    } finally {
      setIsChangingPassword(false);
    }
  };

  const displayRole = getDisplayRole();
  const isAdmin = displayRole === 'Administrator';
  const displayEmail = editedEmail || user?.email || '';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Profile</h2>
          <p className="text-gray-500 mt-1">View and manage your account information.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchUserSettings}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700"
          >
            <Edit2 className="w-4 h-4" />
            Edit Profile
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={handleSaveProfile}
              className="flex items-center gap-2 px-4 py-2 bg-[#0369A1] text-white rounded-lg hover:bg-gray-700 text-sm font-medium"
            >
              <Save className="w-4 h-4" />
              Save
            </button>
            <button
              onClick={handleCancelEdit}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
          </div>
        )}
        </div>
      </div>

      {saveMessage && (
        <div
          className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
            saveStatus === 'error'
              ? 'bg-red-50 border border-red-200 text-red-700'
              : 'bg-green-50 border border-green-200 text-green-700'
          }`}
        >
          {saveStatus === 'error' ? (
            <AlertTriangle className="w-4 h-4" />
          ) : (
            <CheckCircle className="w-4 h-4" />
          )}
          {saveMessage}
        </div>
      )}

      <div className="bg-gray-100 rounded-xl p-6 shadow-sm border border-gray-100">
        {/* Profile Header */}
        <div className="flex items-center space-x-4 pb-6 border-b border-gray-100">
          <div className="w-20 h-20 rounded-full bg-[#0369A1] flex items-center justify-center text-white text-2xl font-bold">
            {displayEmail.charAt(0).toUpperCase() || user?.username?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900">
              {displayEmail || user?.username || 'User'}
            </h3>
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              isAdmin ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
            }`}>
              <Shield className="w-3 h-3" />
              {displayRole}
            </span>
          </div>
        </div>

        {/* Profile Details */}
        <div className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 text-gray-700">
              <Mail className="w-5 h-5 text-gray-400" />
              <span className="text-sm font-medium">Email:</span>
            </div>
            {isEditing ? (
              <input
                type="email"
                value={editedEmail}
                onChange={(e) => setEditedEmail(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 w-64"
              />
            ) : (
              <span className="text-sm text-gray-900">{displayEmail || 'Not set'}</span>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 text-gray-700">
              <Building className="w-5 h-5 text-gray-400" />
              <span className="text-sm font-medium">Tenant:</span>
            </div>
            <span className="text-sm text-gray-900">{user?.tenant_id || 'default'}</span>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 text-gray-700">
              <Shield className="w-5 h-5 text-gray-400" />
              <span className="text-sm font-medium">Role:</span>
            </div>
            <span className={`text-sm font-medium ${isAdmin ? 'text-purple-700' : 'text-gray-900'}`}>
              {displayRole}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 text-gray-700">
              <Calendar className="w-5 h-5 text-gray-400" />
              <span className="text-sm font-medium">Member since:</span>
            </div>
            <span className="text-sm text-gray-900">{memberSince ?? 'Loading…'}</span>
          </div>
        </div>
      </div>

      {/* Security Section */}
      <div className="bg-gray-100 rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="font-semibold text-gray-900 mb-4">Security</h3>
        <div className="space-y-4">
          <button
            onClick={() => setShowPasswordModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#0369A1] text-white rounded-lg hover:bg-gray-700 text-sm font-medium"
          >
            <Key className="w-4 h-4" />
            Change Password
          </button>
          <p className="text-xs text-gray-500">
            Password must be at least 8 characters with uppercase, lowercase, and numbers.
          </p>
        </div>
      </div>

      {/* Data Source Permissions Section */}
      <div className="bg-gray-100 rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Database className="w-5 h-5 text-purple-600" />
              Data Source Access
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {isAdmin ? 'Configure which data sources you can access' : 'Your permitted data sources'}
            </p>
          </div>
          <span className="text-sm text-purple-600 font-medium">
            {dataSourcePermissions.length} of {AVAILABLE_DATA_SOURCES.length} enabled
          </span>
        </div>

        <div className="space-y-3">
          {/* Group by collection */}
          {['Electricity Data', 'Environmental Data', 'Financial Data'].map(collection => {
            const sources = AVAILABLE_DATA_SOURCES.filter(s => s.collection === collection);
            return (
              <div key={collection} className="border border-gray-100 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
                  <h4 className="text-sm font-medium text-gray-700">{collection}</h4>
                </div>
                <div className="divide-y divide-gray-100">
                  {sources.map(source => {
                    const Icon = source.icon;
                    const hasAccess = dataSourcePermissions.includes(source.id);

                    return (
                      <div
                        key={source.id}
                        className={`flex items-center justify-between px-4 py-3 transition-colors ${
                          isAdmin ? 'cursor-pointer hover:bg-gray-50' : ''
                        }`}
                        onClick={() => isAdmin && toggleDataSourcePermission(source.id)}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            hasAccess ? 'bg-purple-100' : 'bg-gray-100'
                          }`}>
                            <Icon className={`w-4 h-4 ${hasAccess ? 'text-purple-600' : 'text-gray-400'}`} />
                          </div>
                          <div>
                            <p className={`text-sm font-medium ${hasAccess ? 'text-gray-900' : 'text-gray-500'}`}>
                              {source.name}
                            </p>
                            <p className="text-xs text-gray-400">
                              {sourceCounts === null
                                ? 'Checking…'
                                : (sourceCounts[SOURCE_DATASTORE_KEYS[source.id]] ?? 0) > 0
                                  ? 'Active'
                                  : 'Coming Soon'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {hasAccess ? (
                            <span className="flex items-center gap-1 text-xs px-2 py-1 bg-green-50 text-green-600 rounded-full">
                              <Unlock className="w-3 h-3" />
                              Enabled
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs px-2 py-1 bg-gray-100 text-gray-500 rounded-full">
                              <Lock className="w-3 h-3" />
                              Disabled
                            </span>
                          )}
                          {isAdmin && (
                            <input
                              type="checkbox"
                              checked={hasAccess}
                              onChange={() => toggleDataSourcePermission(source.id)}
                              className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                              onClick={e => e.stopPropagation()}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {!isAdmin && (
          <p className="text-xs text-gray-500 mt-4">
            Contact your administrator to request access to additional data sources.
          </p>
        )}
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-100 rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h3>

            {passwordError && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                {passwordError}
              </div>
            )}

            {passwordSuccess && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                <CheckCircle className="w-4 h-4" />
                {passwordSuccess}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                <input
                  type="password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter current password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter new password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Confirm new password"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleChangePassword}
                disabled={isChangingPassword}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[#0369A1] text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 text-sm font-medium"
              >
                {isChangingPassword ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Changing...</>
                ) : (
                  <><Key className="w-4 h-4" /> Change Password</>
                )}
              </button>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setOldPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                  setPasswordError('');
                  setPasswordSuccess('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
